<template>
    <BaseModal
        size="modal-lg"
        class="cancel-order"
        :closable="true"
        ref="baseModalComponent"
        :title="headerTitle"
        :has-header="true"
    >
        <template v-slot:body>
            <div class="cancel-whole-order-text">
                <span v-if="!isOrderLoading">{{ translations.cancellation.wholeOrderDesc }}</span>
                <span v-else>{{ translations.cancellation.loading }}</span>
            </div>
        </template>

        <template v-slot:footer>
            <div class="refund-toggle-container" v-if="showRefundToggle">
                <InputSwitch v-model="operatorRefund" />
                <span class="label">{{ translations.cancellation.refund }}</span>
            </div>
            <button type="button" class="btn btn-danger" :disabled="cancelButtonDisabled" @click="cancelWholeOrder">
                {{ cancelButtonText }}
            </button>
        </template>
    </BaseModal>
</template>
<script setup lang="ts">
    import type { IAddCancellationCmd } from "@/data/commands/order/AddCancellationCmd";
    import { Factory } from "@/data/dao/Factory";
    import { OrderDao } from "@/data/dao/service/OrderDao";
    import { CancellationMode, RefundMode, RefundabilityType } from "@/data/model/Enums";
    import type { IFullOrder } from "@/data/model/Order";
    import { UiNotification } from "@/portal/ui/Notification";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { createEmptyDiv, createVueComponent } from "@/shared/utils/vue";
    import { computed, onMounted, ref } from "vue";

    import BaseModal from "@/components/dialog/BaseModal.vue";
    import CancellationReasonModal from "@/components/order/CancellationReasonModal.vue";
    import InputSwitch from "primevue/inputswitch";

    const props = defineProps<{
        locationId: string;
        orderId: string;
        onOrderCancellationSucceeded?: () => void;
        onOrderCancellationFailed?: (errorMessage: string) => void;
    }>();

    const orderDao = Factory.getInstance<OrderDao>(OrderDao);

    const cancelButtonText = computed(() => {
        return `${translations.cancellation.submitBtn} - CHF ${cancellableAmount.value.toFixed(2)}`;
    });

    const showRefundToggle = computed(() => {
        return currentOrder.value?.refundabilityType === RefundabilityType.RefundOptionalForOperators;
    });

    const isOrderLoading = ref<boolean>(false);
    const operatorRefund = ref<boolean>(false);
    const baseModalComponent = ref<InstanceType<typeof BaseModal> | null>(null);
    const cancelButtonDisabled = ref(false);
    const cancellableAmount = ref<number>(0);
    const currentOrder = ref<IFullOrder | null>(null);

    const headerTitle = computed(() => {
        return `${translations.cancellation.cancellation} - ${translations.enums.cancellationMode[CancellationMode[CancellationMode.WholeOrder]]}`;
    });

    async function cancelWholeOrder(): Promise<void> {
        const data: IAddCancellationCmd = {
            refundMode: RefundMode.Online,
            mode: CancellationMode.WholeOrder,
            locationId: props.locationId,
            isRefundApprovedByOperator: operatorRefund.value,
            positions: undefined,
        };

        const result = createVueComponent(
            CancellationReasonModal,
            {
                locationId: props.locationId,
                orderId: props.orderId,
                data: data,
                cancellableAmount: cancellableAmount.value,
                onSaveError: (errorCode: string, errorMessage: string) => {
                    UiNotification.showException(errorMessage, undefined, () => {
                        if (props.onOrderCancellationFailed) {
                            props.onOrderCancellationFailed(errorMessage);
                        }
                        close();
                    });
                },
                onSaveEnd: () => {
                    if (props.onOrderCancellationSucceeded) {
                        props.onOrderCancellationSucceeded();
                    }
                    close();
                },
            },
            createEmptyDiv()
        );
        const modal = result.mountedComponent;
        modal.show();
    }

    onMounted(async () => {
        try {
            isOrderLoading.value = true;
            let order = await orderDao.getSingle(props.orderId, { showLocationOrderItemsIfLocationIsNotOwner: true });
            cancellableAmount.value = order.totalAmount - order.totalCancellationAmount;
            currentOrder.value = order;
        } catch (error) {
            logError(error);
            UiNotification.showException(translations.general.failedLoadingData, undefined, () => {
                close();
            });
        } finally {
            isOrderLoading.value = false;
        }
    });

    function show() {
        baseModalComponent.value?.show();
    }

    function close() {
        baseModalComponent.value?.close();
    }

    defineExpose({
        show,
        close,
    });
</script>

<style scoped lang="scss">
    .cancel-whole-order-text {
        text-align: center;

        span {
            padding: 1em 0;
            font-size: 14px;
        }
    }

    .refund-toggle-container {
        display: flex;
        gap: 12px;
        flex: auto;

        > .label {
            font-weight: 500;
        }
    }
</style>
